import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import classNames from "classnames";
import { useI18n } from "i18n";
import { MAGIC_LINK_EMAIL_KEY } from "modules/auth/constants";
import { AuthBottomBlock } from "modules/auth/layout/AuthBottomBlock";
import { AuthTopBlock } from "modules/auth/layout/AuthTopBlock";
import AuthViewContainer from "modules/auth/layout/AuthViewContainer";
import AuthViewContent from "modules/auth/layout/AuthViewContent";
import isSignInWithEmailLink from "modules/auth/utils/isSignInWithEmailLink";
import signInWithEmailLink from "modules/auth/utils/signInWithEmailLink";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { PAGES, usePageTracking } from "modules/tracking";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import MagicLinkSignInForm from "../MagicLinkSignInForm";
import { useStyles } from "./styles";

const MagicLinkCallback = () => {
  const { t } = useI18n(["common", "auth"]);
  const [isLoading, setIsLoading] = useState(true);
  const styles = useStyles();
  const { addErrorNotification } = useNotificationActions();
  const { push } = useHistory();
  const { redirectTo } = useQueryParams();

  usePageTracking(PAGES.MAGIC_LINK_REDIRECT);

  const redirectToSignIn = () => {
    push({ pathname: "/signin", search: `redirectTo=${redirectTo}` });
  };

  const signIn = async (email: string) => {
    setIsLoading(true);

    try {
      await signInWithEmailLink(email);
    } catch (error) {
      if (error instanceof Error) {
        addErrorNotification({
          message: t("auth:magic.link.invalid"),
          autoDismiss: 10,
        });
      }
      redirectToSignIn();
    }
  };

  useEffect(() => {
    const tryToSignIn = async () => {
      if (isSignInWithEmailLink()) {
        const email = localStorage.getItem(MAGIC_LINK_EMAIL_KEY);

        if (email) {
          await signIn(email);
          localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
        } else {
          setIsLoading(false);
        }
      } else {
        addErrorNotification({
          message: t("auth:magic.link.invalid"),
          autoDismiss: 10,
        });
        redirectToSignIn();
      }
    };

    tryToSignIn();

    // this effect should only be run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isLoading ? (
    <PageLoader message={t("wait")} data-testid="loading" />
  ) : (
    <AuthViewContainer>
      <AuthViewContent>
        <AuthTopBlock />
        <AuthBottomBlock>
          <div className={classNames(styles.container, "email-container")}>
            <MagicLinkSignInForm onSubmit={signIn} />
          </div>
        </AuthBottomBlock>
      </AuthViewContent>
    </AuthViewContainer>
  );
};

export default MagicLinkCallback;
